import Link, { type LinkOptions } from '@tiptap/extension-link';
import { type Editor } from '@tiptap/react';
import { Plugin, PluginKey } from 'prosemirror-state';
import { type Root, createRoot } from 'react-dom/client';

import { LinkHoverTooltip } from '../../../routes/app/content/-editor/link-hover-tooltip';
import { isExternalLink } from '../index';

export type ContentLinkOptions = LinkOptions & {
  onEditLink?: (href: string, text: string) => void;
  onUnlinkLink?: () => void;
  onHoverStart?: () => void;
  onHoverEnd?: () => void;
};

const linkHoverPluginKey = new PluginKey('linkHover');

function createLinkHoverPlugin(options: {
  editor: Editor;
  onEditLink?: (href: string, text: string) => void;
  onUnlinkLink?: () => void;
  onHoverStart?: () => void;
  onHoverEnd?: () => void;
}) {
  let tooltipElement: HTMLDivElement | null = null;
  let tooltipRoot: Root | null = null;
  let hideTimeout: ReturnType<typeof setTimeout> | null = null;
  let currentLinkElement: HTMLAnchorElement | null = null;
  let currentLinkPosition: number | null = null;

  function hideTooltip() {
    if (hideTimeout != null) {
      clearTimeout(hideTimeout);
      hideTimeout = null;
    }
    if (tooltipRoot != null) {
      tooltipRoot.unmount();
      tooltipRoot = null;
    }
    if (tooltipElement != null) {
      tooltipElement.remove();
      tooltipElement = null;
      if (typeof options.onHoverEnd === 'function') {
        options.onHoverEnd();
      }
    }
    currentLinkElement = null;
    currentLinkPosition = null;
  }

  function scheduleHide() {
    if (hideTimeout != null) {
      clearTimeout(hideTimeout);
    }
    hideTimeout = setTimeout(function () {
      hideTooltip();
    }, 150);
  }

  function selectLinkAtPosition() {
    if (currentLinkPosition == null) {
      return;
    }
    // Focus the editor and select the link mark range
    options.editor
      .chain()
      .focus()
      .setTextSelection(currentLinkPosition)
      .extendMarkRange('link')
      .run();
  }

  function showTooltip(linkElement: HTMLAnchorElement, href: string, text: string, pos: number) {
    if (tooltipElement != null && currentLinkElement === linkElement) {
      return;
    }

    hideTooltip();

    if (typeof options.onHoverStart === 'function') {
      options.onHoverStart();
    }
    currentLinkElement = linkElement;
    currentLinkPosition = pos;

    tooltipElement = document.createElement('div');
    tooltipElement.style.position = 'absolute';
    tooltipElement.style.zIndex = '50';
    tooltipElement.setAttribute('data-link-hover-tooltip', 'true');

    const rect = linkElement.getBoundingClientRect();
    const scrollTop = window.scrollY !== 0 ? window.scrollY : document.documentElement.scrollTop;
    const scrollLeft = window.scrollX !== 0 ? window.scrollX : document.documentElement.scrollLeft;

    const tooltipHeight = 40;
    const spacing = 8;
    // Position at center of link, then use transform to center the tooltip
    const linkCenterX = rect.left + scrollLeft + rect.width / 2;
    tooltipElement.style.top = `${rect.top + scrollTop - tooltipHeight - spacing}px`;
    tooltipElement.style.left = `${linkCenterX}px`;
    tooltipElement.style.transform = 'translateX(-50%)';

    document.body.appendChild(tooltipElement);

    tooltipRoot = createRoot(tooltipElement);
    tooltipRoot.render(
      LinkHoverTooltip({
        href,
        text,
        onEdit: function () {
          selectLinkAtPosition();
          if (typeof options.onEditLink === 'function') {
            options.onEditLink(href, text);
          }
          hideTooltip();
        },
        onUnlink: function () {
          selectLinkAtPosition();
          if (typeof options.onUnlinkLink === 'function') {
            options.onUnlinkLink();
          }
          hideTooltip();
        },
      })
    );

    tooltipElement.addEventListener('mouseenter', function () {
      if (hideTimeout != null) {
        clearTimeout(hideTimeout);
        hideTimeout = null;
      }
    });

    tooltipElement.addEventListener('mouseleave', function () {
      scheduleHide();
    });
  }

  function handleScroll() {
    hideTooltip();
  }

  return new Plugin({
    key: linkHoverPluginKey,
    props: {
      handleDOMEvents: {
        mouseover: function (view, event) {
          const target = event.target as HTMLElement;
          const linkElement = target.closest('a');

          if (linkElement != null && linkElement.href !== '') {
            if (hideTimeout != null) {
              clearTimeout(hideTimeout);
              hideTimeout = null;
            }
            // Get the position of the link in the document
            const pos = view.posAtDOM(linkElement, 0);
            const linkText = linkElement.textContent ?? '';
            showTooltip(linkElement, linkElement.href, linkText, pos);
          }
          return false;
        },
        mouseout: function (_view, event) {
          const target = event.target as HTMLElement;
          const relatedTarget = event.relatedTarget as HTMLElement | null;
          const linkElement = target.closest('a');

          if (linkElement != null) {
            const isMovingToTooltip = relatedTarget?.closest('[data-link-hover-tooltip]') != null;
            const isMovingToSameLink = relatedTarget?.closest('a') === linkElement;

            if (!isMovingToTooltip && !isMovingToSameLink) {
              scheduleHide();
            }
          }
          return false;
        },
        scroll: function () {
          hideTooltip();
          return false;
        },
      },
    },
    view: function () {
      window.addEventListener('scroll', handleScroll, true);
      return {
        destroy: function () {
          window.removeEventListener('scroll', handleScroll, true);
          hideTooltip();
        },
      };
    },
  });
}

export function createContentLink(
  website?: string | null,
  onEditLink?: (href: string, text: string) => void,
  onUnlinkLink?: () => void,
  onHoverStart?: () => void,
  onHoverEnd?: () => void
) {
  return Link.configure({
    openOnClick: false,
    defaultProtocol: 'https',
    protocols: ['http', 'https'],
    HTMLAttributes: {
      rel: null,
      target: null,
    },
  }).extend<ContentLinkOptions>({
    addOptions() {
      return {
        ...(this.parent?.() ?? {}),
        onEditLink,
        onUnlinkLink,
        onHoverStart,
        onHoverEnd,
      };
    },
    addAttributes() {
      return {
        ...this.parent?.(),
        target: {
          default: null,
          parseHTML: function (element) {
            return element.getAttribute('target');
          },
          renderHTML: function (attributes) {
            if (attributes.target == null) {
              return {};
            }
            return {
              target: attributes.target as string,
            };
          },
        },
        rel: {
          default: null,
          parseHTML: function () {
            return null;
          },
          renderHTML: function () {
            return {};
          },
        },
      };
    },
    renderHTML({ HTMLAttributes }) {
      const href = (HTMLAttributes.href as string | null | undefined) ?? null;
      const isExternal = isExternalLink(href, website ?? '');

      const targetAttribute = (HTMLAttributes.target as string | null | undefined) ?? null;
      const target = targetAttribute ?? (isExternal ? '_blank' : null);

      return [
        'a',
        {
          ...HTMLAttributes,
          target,
          rel: null,
        },
        0,
      ];
    },
    addProseMirrorPlugins() {
      const parentPlugins = this.parent?.() ?? [];
      return [
        ...parentPlugins,
        createLinkHoverPlugin({
          editor: this.editor,
          onEditLink: this.options.onEditLink,
          onUnlinkLink: this.options.onUnlinkLink,
          onHoverStart: this.options.onHoverStart,
          onHoverEnd: this.options.onHoverEnd,
        }),
      ];
    },
  });
}
